import React from 'react';
import { Box, TextField, Typography, Alert, Autocomplete, Chip } from '@mui/material';
import { AccountTree as SubPresetIcon } from '@mui/icons-material';

import { useImpactAiPresetsQuery } from 'generated/graphql';

import { SubPresetAction, ImpactAiPreset } from '../../../types';

interface SubPresetActionEditorProps {
  action: SubPresetAction;
  onChange: (action: SubPresetAction) => void;
}

const SubPresetActionEditor: React.FC<SubPresetActionEditorProps> = ({ action, onChange }) => {
  // Get available presets for dropdown selection
  const { data: presetsData } = useImpactAiPresetsQuery({
    fetchPolicy: 'cache-first',
  });

  const availablePresets: ImpactAiPreset[] =
    (presetsData?.impactAiPresets?.items as ImpactAiPreset[]) || [];

  // Find the selected preset for display
  const selectedPreset = availablePresets.find((p) => p.id === action.data.presetId);

  const handleChange = (field: keyof SubPresetAction['data'], value: any) => {
    onChange({
      ...action,
      data: {
        ...action.data,
        [field]: value,
      },
    });
  };

  const handlePresetSelect = (preset: ImpactAiPreset | null) => {
    handleChange('presetId', preset?.id || '');
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Alert severity="info" icon={<SubPresetIcon />}>
        <Typography variant="body2">
          <strong>Sub Preset Actions</strong> allow you to include all actions from another preset.
          When this preset runs, the selected preset&apos;s actions will be executed in place of
          this action.
        </Typography>
      </Alert>

      <Autocomplete
        value={selectedPreset || null}
        onChange={(_, value) => handlePresetSelect(value)}
        options={availablePresets}
        getOptionLabel={(preset) => preset.title}
        renderOption={(props, preset) => (
          <Box component="li" {...props}>
            <Box display="flex" flexDirection="column" width="100%">
              <Typography variant="body2" fontWeight={500}>
                {preset.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {preset.description} • {preset.actions?.length || 0} actions
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Preset"
            placeholder="Choose a preset to include..."
            helperText="Select an existing preset whose actions will be included here"
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((preset, index) => (
            <Chip
              label={preset.title}
              size="small"
              {...getTagProps({ index })}
              key={`${preset.id}`}
            />
          ))
        }
      />

      {/* Manual preset ID input as fallback */}
      <TextField
        label="Preset ID (Manual)"
        value={action.data.presetId}
        onChange={(e) => handleChange('presetId', e.target.value)}
        fullWidth
        size="small"
        helperText="Alternatively, enter a preset ID directly"
        placeholder="e.g., 507f1f77bcf86cd799439011"
      />

      {selectedPreset && (
        <Alert severity="success">
          <Typography variant="body2">
            <strong>Selected:</strong> &quot;{selectedPreset.title}&quot;
            {selectedPreset.actions && ` (${selectedPreset.actions.length} actions)`}
          </Typography>
          {selectedPreset.description && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {selectedPreset.description}
            </Typography>
          )}
        </Alert>
      )}

      {action.data.presetId && !selectedPreset && (
        <Alert severity="warning">
          <Typography variant="body2">
            <strong>Warning:</strong> Preset with ID &quot;{action.data.presetId}&quot; not found in
            available presets. Make sure the preset exists and is accessible. The Related Presets
            controls are at the bottom of this page. Scroll down to see them.
          </Typography>
        </Alert>
      )}

      <Alert severity="info">
        <Typography variant="body2">
          <strong>Note:</strong> Sub presets are expanded when the preset runs, so users will see
          the actual actions from the referenced preset, not this sub preset reference.
        </Typography>
      </Alert>
    </Box>
  );
};

export default SubPresetActionEditor;
